import { createReducer, on, type Action } from '@ngrx/store';
import { get } from 'lodash-es';
import { immerOn } from 'ngrx-immer/store';
import { fetchProfile, loginSuccess } from '../../../auth/store/actions';
import {
  getItemFromLocalStorage,
  setItemInLocalStorage,
} from '../../../core/services/local-storage.service';
import { SupplierManagementHelperService } from '../../../core/services/supplier-management-helper.service';
import type { SupplierFilter } from '../../../insights/suppliers/models/supplier.models';
import { deleteAccountSuccess } from '../../../management/accounts/account-delete/store/account-delete-modal.actions';
import type {
  Account,
  Company,
} from '../../../management/accounts/models/account.models';
import {
  createAccountSuccess,
  updateAccountSuccess,
} from '../../../management/accounts/store/actions';
import {
  createCompanySuccess,
  deleteCompanySuccess,
  updateCompanySuccess,
} from '../../../management/companies/store/actions';
import { Modules } from '../../consts/custom-fields.consts';
import { CrudStatus } from '../../consts/intelligence.const';
import { isArray } from '../../helpers/is-array/is-array.helper';
import { isNotNullOrUndefined } from '../../helpers/is-not-null-or-undefined/is-not-null-or-undefined.helpers';
import { readSharedRequestParametersFromStorage } from '../../helpers/read-shared-request-parameters-from-storage/read-shared-request-parameters-from-storage.helper';
import {
  sharedCompanyAndAccountConfiguration,
  type AnyObject,
  type ContentArea,
  type ICompanyAndAccountConfiguration,
} from '../../models/core.interface';
import { PropertyType } from '../../models/property.models';
import type {
  // eslint-disable-next-line unicorn/prevent-abbreviations -- TODO: delete the comment and fix the error
  IRequestParams,
  // eslint-disable-next-line unicorn/prevent-abbreviations -- TODO: delete the comment and fix the error
  ISharedRequestParams,
} from '../../models/request-parameters.models';
import {
  getPropertiesAndFiltersFail,
  getPropertiesAndFiltersSuccess,
  loadAccounts,
  loadAccountsFail,
  loadAccountsSuccess,
  loadCompanies,
  loadCompaniesFail,
  loadCompaniesSuccess,
  resetCompanyAndAccountConfiguration,
  updateAccounts,
  updateCompanies,
  updateCompanyAndAccountConfiguration,
  updateSharedParameters,
  updateSupplierFilters,
  updateSupplierFiltersFail,
  updateSupplierFiltersSuccess,
} from '../actions/company-account.actions';

export interface CompanyAccountState {
  accounts?: Account[];
  companies?: Company[];
  requestParams?: ISharedRequestParams;
  accountsStatus?: CrudStatus;
  companiesStatus?: CrudStatus;
  propertiesResponse?: AnyObject;
  activeFilters?: SupplierFilter[];
  companyAndAccountConfiguration?: ICompanyAndAccountConfiguration;
  contentAreas?: ContentArea[];
}

/**
 * Built on demand rather than captured once at module evaluation. The ngrx
 * logout meta-reducer resets every feature state to `undefined`, so whatever
 * this returns is what a post-logout reset falls back to — a frozen snapshot
 * would resurrect the account selection made when the page was loaded.
 */
const createInitialState = (): CompanyAccountState => {
  const requestParameters = readSharedRequestParametersFromStorage();

  return {
    requestParams: requestParameters,
    companyAndAccountConfiguration: { ...sharedCompanyAndAccountConfiguration },
  };
};

const companyAccountReducer = createReducer(
  createInitialState(),
  on(loadAccounts, (state): CompanyAccountState => {
    return {
      ...state,
      accountsStatus: CrudStatus.PROGRESS,
    };
  }),
  on(loginSuccess, fetchProfile, (state): CompanyAccountState => {
    return {
      ...state,
      // FIXME: refactor
      requestParams: getItemFromLocalStorage('sharedParams'),
    };
  }),
  on(loadAccountsSuccess, (state, action): CompanyAccountState => {
    return {
      ...state,
      accountsStatus: CrudStatus.COMPLETE,
      accounts: get(action.payload, 'data'),
    };
  }),
  on(loadAccountsFail, (state): CompanyAccountState => {
    return {
      ...state,
      accountsStatus: CrudStatus.FAIL,
    };
  }),
  on(updateAccounts, (state, action): CompanyAccountState => {
    return {
      ...state,
      accounts: [...action.payload],
    };
  }),
  on(updateSharedParameters, (state, action): CompanyAccountState => {
    const accounts = action.payload?.accounts;
    const company_id = action.payload?.company_id;
    const hasOnlyOneAccount = action.payload.hasOnlyOneAccount;

    // FIXME: refactor
    setItemInLocalStorage('sharedParams', {
      accounts,
      company_id,
      hasOnlyOneAccount,
    });

    return {
      ...state,
      requestParams: {
        ...state.requestParams,
        accounts,
        company_id,
        hasOnlyOneAccount,
      },
      ...((!accounts || accounts.length === 0) && {
        propertiesResponse: {},
        activeFilters: [],
      }),
    };
  }),
  on(loadCompanies, (state): CompanyAccountState => {
    return {
      ...state,
      companiesStatus: CrudStatus.PROGRESS,
    };
  }),
  on(loadCompaniesSuccess, (state, action): CompanyAccountState => {
    return {
      ...state,
      companiesStatus: CrudStatus.COMPLETE,
      companies: get(action.payload, 'data'),
    };
  }),
  on(loadCompaniesFail, (state): CompanyAccountState => {
    return {
      ...state,
      companiesStatus: CrudStatus.FAIL,
    };
  }),
  on(updateCompanies, (state, action): CompanyAccountState => {
    return {
      ...state,
      companies: [...action.payload],
    };
  }),
  // Add account to shared accounts
  on(createAccountSuccess, (state, action): CompanyAccountState => {
    const accounts = state.accounts || [];
    const companies = state.companies || [];
    // eslint-disable-next-line unicorn/prevent-abbreviations -- TODO: delete the comment and fix the error
    const requestParams = state.requestParams || { accounts: [] };

    return {
      ...state,
      accounts: [...accounts, action.payload],
      requestParams: {
        ...requestParams,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO: delete the comment and fix the error
        accounts: [...requestParams.accounts!, action.payload.id!],
      },
      companies: companies.map((c) => {
        if (c.id === action.payload.company_id) {
          return { ...c, accounts: [...c.accounts, action.payload] };
        }
        return c;
      }),
    };
  }),
  on(updateAccountSuccess, (state, action): CompanyAccountState => {
    if (state.accounts) {
      const accounts = state.accounts.filter((a) => a.id !== action.payload.id);
      return {
        ...state,
        accounts: [...accounts, action.payload],
        companies:
          isArray(state.companies) ?
            state.companies.map((c) => {
              if (c.id === action.payload.company_id) {
                return { ...c, accounts: [...accounts, action.payload] };
              }
              return c;
            })
          : state.companies,
      };
    }
    return state;
  }),
  immerOn(deleteAccountSuccess, (state, action) => {
    const deletedAccountId = action.deletedAccount.accountId;
    const deletedAccountCompanyId = action.deletedAccount.companyId;

    state.accounts = state.accounts?.filter(
      ({ id }) => id !== deletedAccountId,
    );

    state.companies = state.companies?.map((oneCompany) => {
      if (oneCompany.id !== deletedAccountCompanyId) {
        return oneCompany;
      }

      return {
        ...oneCompany,
        accounts: oneCompany.accounts.filter(
          ({ id }) => id !== deletedAccountId,
        ),
      };
    });

    if (isNotNullOrUndefined(state.requestParams)) {
      state.requestParams.accounts = state.requestParams.accounts?.filter(
        (oneAccountId) => oneAccountId !== deletedAccountId,
      );
    }
  }),
  // Add company to shared companies
  on(createCompanySuccess, (state, action): CompanyAccountState => {
    if (state.companies) {
      return {
        ...state,
        companies: [...state.companies, action.payload.data],
      };
    }
    return state;
  }),
  on(updateCompanySuccess, (state, action): CompanyAccountState => {
    if (state.companies) {
      const companies = state.companies.filter(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: delete the comment and fix the error
        (c) => c.id !== action.payload.data.id,
      );
      return {
        ...state,
        companies: [...companies, action.payload.data],
      };
    }
    return state;
  }),
  on(deleteCompanySuccess, (state, action): CompanyAccountState => {
    if (state.companies) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- TODO: delete the comment and fix the error
      const selectedCompanyId = state.requestParams!.company_id;
      // eslint-disable-next-line unicorn/prevent-abbreviations -- TODO: delete the comment and fix the error
      let requestParams = state.requestParams;
      if (!requestParams || action.payload.data.id === selectedCompanyId) {
        requestParams = { accounts: [] } as unknown as IRequestParams;
      }
      return {
        ...state,
        companies: state.companies.filter(
          (c) => c.id !== action.payload.data.id,
        ),
        requestParams: {
          ...requestParams,
        },
      };
    }
    return state;
  }),
  on(getPropertiesAndFiltersSuccess, (state, action): CompanyAccountState => {
    // FIXME: refactor
    const profile = getItemFromLocalStorage('profile');

    const contentAreas = (profile || { contentAreas: [] }).contentAreas;
    const defaultFilters = (profile || { defaultFilters: [] }).defaultFilters;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: delete the comment and fix the error
    const categoryTrees = action.payload?.categoryTrees?.data || [];
    const categoriesAreaId = contentAreas.find(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call -- TODO: delete the comment and fix the error
      (area: any) => area.name.toLowerCase() === Modules.Categories,
    );

    let properties = action.payload?.properties?.data || {};

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: delete the comment and fix the error
    let activeFilters =
      /* eslint-disable @typescript-eslint/no-unsafe-member-access -- TODO: delete the comment and fix the error, @typescript-eslint/no-unsafe-call */
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      (action.payload.activeFilters.data?.filters || []).filter(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: delete the comment and fix the error
        (f: any) =>
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
          Object.values(PropertyType).includes(f.type) ||
          f.module === Modules.Categories,
      );
    /* eslint-enable @typescript-eslint/no-unsafe-member-access */
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: delete the comment and fix the error
    activeFilters =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: delete the comment and fix the error
      activeFilters.length > 0 ?
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-explicit-any, @typescript-eslint/no-unsafe-return -- TODO: delete the comment and fix the error
        activeFilters.map((f: any) => ({ ...f, active: true }))
      : defaultFilters;
    properties =
      SupplierManagementHelperService.instance.removeBlacklistedProperties(
        properties,
      );
    properties =
      SupplierManagementHelperService.instance.markActiveStaticFilters(
        activeFilters,
        properties,
      );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- TODO: delete the comment and fix the error
    if (categoriesAreaId && categoryTrees.length > 0) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, unicorn/prevent-abbreviations -- TODO: delete the comment and fix the error
      const activeFiltersObj =
        SupplierManagementHelperService.instance.supplierFiltersAsObj(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- TODO: delete the comment and fix the error
          activeFilters,
        );

      properties = {
        ...properties,
        [categoriesAreaId.id]:
          SupplierManagementHelperService.instance.markActiveCategories(
            activeFiltersObj,
            categoryTrees,
          ),
      };
    }

    return {
      ...state,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: delete the comment and fix the error
      propertiesResponse: { properties, activeFilters },
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- TODO: delete the comment and fix the error
      activeFilters,
    };
  }),

  on(updateSupplierFilters, (state): CompanyAccountState => {
    return {
      ...state,
      // @ts-expect-error TODO: delete the comment and fix the error
      filtersCrud: CrudStatus.PROGRESS,
    };
  }),
  on(updateSupplierFiltersFail, (state): CompanyAccountState => {
    return {
      ...state,
      // @ts-expect-error TODO: delete the comment and fix the error
      filtersCrud: CrudStatus.FAIL,
    };
  }),
  on(updateSupplierFiltersSuccess, (state, action): CompanyAccountState => {
    // FIXME: refactor
    const profile = getItemFromLocalStorage('profile');

    const defaultFilters = (profile || { defaultFilters: [] }).defaultFilters;
    const activeFilters =
      (action.payload.filters || []).length > 0 ?
        action.payload.filters
      : defaultFilters;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- supplierFiltersAsObj returns untyped object
    const activeFiltersObject =
      SupplierManagementHelperService.instance.supplierFiltersAsObj(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument -- defaultFilters from localStorage is untyped
        activeFilters,
      );

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment -- propertiesResponse structure is loosely typed
    const currentProperties = state.propertiesResponse?.['properties'];

    const updatedProperties: Record<string, unknown[]> = {};
    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/prefer-nullish-coalescing -- currentProperties structure is loosely typed from API
    for (const propertyKey of Object.keys(currentProperties || {})) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access -- currentProperties is loosely typed from API
      const propertyArray = currentProperties?.[propertyKey];
      if (isArray(propertyArray)) {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-explicit-any -- Properties are dynamically typed from API
        updatedProperties[propertyKey] = propertyArray.map((item: any) => ({
          ...item,
          // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- uniqueId exists but item is untyped
          active: !!activeFiltersObject?.[item.uniqueId],
        }));
      }
    }

    return {
      ...state,
      // @ts-expect-error filtersCrud exists at runtime but may not be in type definition
      filtersCrud: CrudStatus.COMPLETE,
      propertiesResponse: {
        properties: updatedProperties,
        activeFilters,
      },
      activeFilters,
    };
  }),
  on(getPropertiesAndFiltersFail, (state): CompanyAccountState => {
    return {
      ...state,
      propertiesResponse: {},
    };
  }),
  on(
    updateCompanyAndAccountConfiguration,
    (state, action): CompanyAccountState => {
      return {
        ...state,
        companyAndAccountConfiguration: {
          ...sharedCompanyAndAccountConfiguration,
          ...action.payload,
        },
      };
    },
  ),
  on(resetCompanyAndAccountConfiguration, (state): CompanyAccountState => {
    return {
      ...state,
      companyAndAccountConfiguration: {
        ...sharedCompanyAndAccountConfiguration,
      },
    };
  }),
);

export const reducer = (
  state: CompanyAccountState | undefined,
  action: Action,
): CompanyAccountState => {
  return companyAccountReducer(state ?? createInitialState(), action);
};

export const COMPANY_ACCOUNT_STORE_KEY = 'CompanyAccount';
